import "./index.less";
import * as React from "react";
import {
    AppScope,
    ClientStore,
    GlobalStores,
    IReactSelectOptionObject,
    ITransactionLogFilters,
    PartnerStore,
    Transaction,
    TransactionStore,
    TransactionType,
    UnhandledScopeError,
} from "@atman/business";
import { AtAutoCompleteSelectField } from "../AtAutoCompleteSelectField";
import { AtButton } from "@atman/design-system";
import { BaseColumn, BaseItemList, ISortElement } from "../BaseItemList";
import { CustomDateRangePicker, IDatePreset } from "../CustomDateRangePicker";
import { FocusedInputShape } from "react-dates";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IRoutedAppContext, withRoutedAppContext } from "../../contexts";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Moment } from "moment";
import { StaticContext } from "react-router";
import { StylesConfig } from "react-select/src/styles";
import {
    TransactionDateColumn,
    TransactionOtherPartyOwnerNameColumn,
    TransactionProductColumn,
    TransactionTypeColumn,
    TransactionUserColumn,
} from "./components/TableColumns";
import { TransactionExpandRow } from "./components/TransactionExpandRow";
import { action, computed, observable, reaction, runInAction } from "mobx";
import { inject, observer } from "mobx-react";
import autobind from "autobind-decorator";
import moment from "moment";

export interface ITransactionsListProps
    extends IRoutedAppContext<{}, StaticContext, { ownerId?: string; transactionType?: TransactionType }> {
    subView?: boolean;
    transactionStore?: TransactionStore;
    clientStore?: ClientStore;
    partnerStore?: PartnerStore;
}

// We're hiding those 2 items since they will be displayed on the Usage/Dashboard page instead;
const transactionTypesToHide: TransactionType[] = [
    TransactionType.ProductReturned,
    TransactionType.ProductUsed,
    TransactionType.Purchase,
];

@inject(GlobalStores.transactionStore, GlobalStores.clientStore, GlobalStores.partnerStore)
@observer
class TransactionsListComp extends React.Component<ITransactionsListProps, {}> {
    @observable public startDate: Moment | null;
    @observable public endDate: Moment | null;
    @observable public focusedInput: FocusedInputShape | null = null;
    @observable public transactionTypeFilter?: IReactSelectOptionObject;
    @observable public otherPartyOwnerIdFilter?: IReactSelectOptionObject;
    @observable public sortDirection?: "asc" | "desc";
    @observable public filteredTransactions: Transaction[] = [];
    @observable public isAtBottom: boolean = false;
    @observable public previousFetchOffset: number = 0;
    @observable public isLoading: boolean = false;

    protected dateReaction = reaction(
        () => this.startDate && this.endDate,
        () => this.refreshTransactions(),
    );
    protected transactionTypeFilterReaction = reaction(
        () => this.transactionTypeFilter,
        () => this.refreshTransactions(),
    );
    protected otherPartyOwnerIdFilterReaction = reaction(
        () => this.otherPartyOwnerIdFilter,
        () => this.refreshTransactions(),
    );
    protected sortDirectionReaction = reaction(
        () => this.sortDirection,
        () => this.refreshTransactions(),
    );

    constructor(props: ITransactionsListProps) {
        super(props);

        if (props.scope !== AppScope.Client) {
            this.startDate = moment().startOf("month");
            this.endDate = moment().endOf("month");
        }
    }

    async componentDidMount() {
        await this.props.transactionStore!.loadTransactionLog();
        this.filteredTransactions = this.props.transactionStore!.transactions;

        if (this.props.scope !== AppScope.Client) {
            this.startDate = moment().startOf("month");
            this.endDate = moment().endOf("month");
        }

        if (this.props.location.state) {
            if (this.props.location.state.ownerId) {
                const match = this.otherPartySelectOptions.find((x) => x.value === this.props.location.state.ownerId);

                if (match) {
                    runInAction(() => {
                        this.otherPartyOwnerIdFilter = match;
                    });
                }
            }

            if (this.props.location.state.transactionType) {
                const match = this.transactionTypeSelectOptions.find(
                    (x) => x.value === String(this.props.location.state.transactionType),
                );

                if (match) {
                    runInAction(() => {
                        this.transactionTypeFilter = match;
                    });
                }
            }
        }
    }

    @computed
    get otherPartySelectOptions(): IReactSelectOptionObject[] {
        const { scope, clientStore, partnerStore } = this.props;

        switch (scope) {
            case AppScope.Client:
                return [];
            case AppScope.Partner:
                return clientStore!.clients.map((x) => ({ label: x.name, value: x.id }));
            case AppScope.Supplier:
                return partnerStore!.partners.map((x) => ({ label: x.name, value: x.id }));
            default:
                throw new UnhandledScopeError(scope);
        }
    }

    @computed
    get transactionTypeSelectOptions(): IReactSelectOptionObject[] {
        const { scope } = this.props;

        let options = Object.keys(TransactionType)
            .filter((x) => !isNaN(Number(x)))
            .filter(
                (x) => !transactionTypesToHide.some((tt) => tt === Number(x)) || Number(x) === TransactionType.Purchase,
            );

        if (scope === AppScope.Supplier) {
            options = options.filter((x) => Number(x) !== TransactionType.Purchase);
        }

        return options.map((x) => ({ label: `global.${TransactionType[x].toCamel()}`.localize(), value: x }));
    }

    @computed
    private get filters(): ITransactionLogFilters {
        const filters: ITransactionLogFilters = {
            startDate: this.startDate ? this.startDate.startOf("D").toISOString(true) : undefined,
            endDate: this.endDate ? this.endDate.startOf("D").toISOString(true) : undefined,
            otherPartyOwnerId: this.otherPartyOwnerIdFilter ? this.otherPartyOwnerIdFilter.value : undefined,
        };

        if (this.transactionTypeFilter && this.transactionTypeFilter.value) {
            if (Number(this.transactionTypeFilter.value) === TransactionType.Purchase) {
                // We do this to mimic a filter to get purchases, even though the Purchase TransactionType doesn't exist in the Back-End
                filters.transactionType = TransactionType.Sale;
                filters.excludeOtherParty = true;
            } else {
                filters.transactionType = TransactionType[this.transactionTypeFilter.value];
            }
        }

        return filters;
    }

    @action.bound
    private refreshTransactions = async () => {
        let filteredTransactions = await this.props.transactionStore!.searchTransactionLog(
            this.filters,
            100,
            0,
            this.sortDirection,
        );

        if (this.props.scope !== AppScope.Client) {
            filteredTransactions = filteredTransactions.filter(
                (x) => !transactionTypesToHide.some((tt) => tt === x.transactionType),
            );
        }

        runInAction(() => {
            this.filteredTransactions = filteredTransactions;
        });
    };

    @action.bound
    handleScroll = async (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight;

        if (bottom) {
            this.isAtBottom = true;

            if (!this.isLoading) {
                this.isLoading = true;

                if (this.previousFetchOffset !== this.filteredTransactions.length) {
                    this.previousFetchOffset = this.filteredTransactions.length;

                    const filteredTransactions = await this.props.transactionStore!.searchTransactionLog(
                        undefined,
                        undefined,
                        this.filteredTransactions.length,
                    );

                    runInAction(() => {
                        this.filteredTransactions.push(...filteredTransactions);
                    });
                }

                runInAction(() => {
                    this.isLoading = false;
                });
            }
        } else {
            if (this.isAtBottom) {
                this.isAtBottom = false;
            }

            if (this.isLoading) {
                this.isLoading = false;
            }
        }
    };

    @action.bound
    handleDatesChanged = ({ startDate, endDate }: { startDate: Moment | null; endDate: Moment | null }) => {
        this.startDate = startDate;
        this.endDate = endDate;
    };

    @action.bound
    handleDateFocusChanged = (focusedInput: any) => {
        this.focusedInput = focusedInput;
    };

    @action.bound
    onOtherPartyOwnerIdFilterChange = (option: any) => {
        this.otherPartyOwnerIdFilter = option ? option : undefined;
    };

    @action.bound
    onTransactionTypesFilterChange = (option: any) => {
        this.transactionTypeFilter = option ? option : undefined;
    };

    @autobind
    async handleTransactionLogExport() {
        await this.props.transactionStore!.downloadTransactionLog(this.filters);
    }

    @action.bound
    onTableChange = (type, { sortField, sortOrder, data }) => {
        this.sortDirection = sortOrder;
    };

    @action.bound
    renderExpandRow = (row: Transaction) => <TransactionExpandRow transaction={row} />;

    render() {
        const { subView, scope, transactionStore } = this.props;

        const isClientEnvironment = scope === AppScope.Client;

        const tableColumns: BaseColumn[] = [new TransactionTypeColumn(scope)];

        if (!isClientEnvironment) {
            tableColumns.push(new TransactionOtherPartyOwnerNameColumn(scope));
        }

        tableColumns.push(new TransactionProductColumn(), new TransactionDateColumn(), new TransactionUserColumn());

        const defaultSorted: ISortElement[] = [
            {
                dataField: "transactionDate",
                order: "desc",
            },
        ];

        let otherPartyOwnerIdFilterPlaceholder: string = "";

        switch (scope) {
            case AppScope.Partner:
                otherPartyOwnerIdFilterPlaceholder = "global.clientIdFilterPlaceholder".localize();
                break;
            case AppScope.Supplier:
                otherPartyOwnerIdFilterPlaceholder = "supplierApp.partnerIdFilterPlaceholder".localize();
                break;
        }

        const dateRangePresets: IDatePreset[] = [
            {
                labelOverride: "global.thisYear".localize(),
                start: moment().startOf("year"),
                end: moment().endOf("year"),
            },
            {
                labelOverride: "global.thisMonth".localize(),
                start: moment().startOf("month"),
                end: moment().endOf("month"),
            },
            {
                labelOverride: "global.thisWeek".localize(),
                start: moment().startOf("week"),
                end: moment().endOf("week"),
            },
            {
                labelOverride: "global.today".localize(),
                start: moment().startOf("day"),
                end: moment().endOf("day"),
            },
        ];

        const customReactSelectStyles: StylesConfig = {
            container: (styles) => ({
                ...styles,
                width: 320,
                marginRight: 16,
            }),
            control: (styles) => ({
                ...styles,
                width: 320,
                padding: "2.5px 8px",
            }),
        };

        return (
            <div id="TransactionsList">
                {!isClientEnvironment ? (
                    <div className="list-header">
                        <div className="left-section d-flex flex-column flex-xl-row text-left">
                            <div className="mb-2 mb-xl-0">
                                <CustomDateRangePicker
                                    startDate={this.startDate}
                                    startDateId={"transactionsStartDate"}
                                    endDate={this.endDate}
                                    endDateId={"transactionsEndDate"}
                                    onDatesChange={this.handleDatesChanged}
                                    focusedInput={this.focusedInput}
                                    onFocusChange={this.handleDateFocusChanged}
                                    isOutsideRange={() => false}
                                    hideKeyboardShortcutsPanel
                                    datePresets={dateRangePresets}
                                />
                            </div>
                            <div className="d-flex flex-row">
                                <AtAutoCompleteSelectField
                                    labelKey={"transactionType"}
                                    options={this.transactionTypeSelectOptions}
                                    value={this.transactionTypeFilter}
                                    onChange={this.onTransactionTypesFilterChange}
                                    placeholder={"global.transactionTypeFilterPlaceholder".localize()}
                                    additionalStyles={customReactSelectStyles}
                                    hideLabel
                                    isClearable
                                />
                                {scope !== AppScope.Client ? (
                                    <AtAutoCompleteSelectField
                                        labelKey={"otherParty"}
                                        options={this.otherPartySelectOptions}
                                        value={this.otherPartyOwnerIdFilter}
                                        onChange={this.onOtherPartyOwnerIdFilterChange}
                                        placeholder={otherPartyOwnerIdFilterPlaceholder.localize()}
                                        additionalStyles={customReactSelectStyles}
                                        hideLabel
                                        isClearable
                                    />
                                ) : null}
                            </div>
                        </div>
                        <div className="right-section">
                            <AtButton color={"secondary"} size={"md"} onClick={this.handleTransactionLogExport}>
                                {"global.exportTransactionLog".localize()}
                            </AtButton>
                            <AtButton
                                onClick={this.refreshTransactions}
                                icon={["far", "sync"] as IconProp}
                                size={"md"}
                                isLoading={transactionStore!.isLoading}
                            />
                        </div>
                    </div>
                ) : null}
                <div className={`list${subView ? " sub-view" : ""}`} onScroll={this.handleScroll}>
                    <BaseItemList<Transaction>
                        tableRows={this.filteredTransactions.slice()}
                        tableColumns={tableColumns}
                        selectRow={false}
                        expandRowContent={this.renderExpandRow}
                        defaultSorted={defaultSorted}
                        remote={{ sort: true }}
                        onTableChange={this.onTableChange}
                    />
                    {this.isLoading ? <FontAwesomeIcon icon="spinner" spin /> : null}
                </div>
            </div>
        );
    }
}

const TransactionsList = withRoutedAppContext(TransactionsListComp);

export { TransactionsList };
